import { useCallback, useId, useMemo } from "react";
import { scaleLinear } from "@visx/scale";
import datetime from "config/datetime";
import { useUnitFormatter } from "hooks";
import AreaChartProvider from "components/Charts/AreaChart/AreaChartProvider";
import type { Series } from "components/Charts/AreaChart/AreaChartProvider";
import type { TooltipData } from "components/Charts/AreaChart/TooltipProvider";
import type { XAxisType } from "components/Charts/AreaChart/XAxisProvider";
import type { YAxisType } from "components/Charts/AreaChart/YAxisProvider";
import useParentDimensions from "components/charting/hooks/useParentDimensions";
import useChartSelections from "../hooks/useChartSelections";
import type { LinePointData } from "../types/Chart.types";
import { ONE_REP_MAX_CHART_MARGIN } from "./OneRepMaxChart.config";
import OneRepMaxChartNoData from "./OneRepMaxChartNoData";
import useOneRepMaxChartLines from "./useOneRepMaxChartLines";
import OneRepMaxTooltipCard from "./OneRepMaxTooltipCard";
import OneRepMaxChartHelpers from "./helpers/OneRepMaxChartHelpers";
import useOneRepMaxChartColor from "./useOneRepMaxChartColor";
import OneRepMaxChartVariantLabels from "./OneRepMaxChartVariantLabels";
import useOneRepMaxChartWeightMetric from "./useOneRepMaxChartWeightMetric";

const xAccessor = (d: LinePointData) => new Date(d.x);
const yAccessor = (d: LinePointData) => d.y;

function OneRepMaxChartV2() {
  const { width, height } = useParentDimensions();
  const selections = useChartSelections();
  const { lines: chartLines, aggregationUnit, spanInDays } = useOneRepMaxChartLines();
  const getLineColor = useOneRepMaxChartColor();
  const formatUnit = useUnitFormatter();
  const weightMetric = useOneRepMaxChartWeightMetric();

  const allPoints = useMemo(() => chartLines.flatMap((line) => line.line), [chartLines]);

  // Every series shares one axis; AreaChart scales an axis from its first series, so each carries the full domain.
  const yScale = useMemo(() => {
    const values = allPoints.map(yAccessor);
    const domainMin = selections.showPlotFromZero ? 0 : Math.min(...values);
    return scaleLinear<number>({ domain: [domainMin, Math.max(...values)], nice: true });
  }, [allPoints, selections.showPlotFromZero]);

  const xAxisId = useId();
  const yAxisId = useId();

  const series = useMemo<Series<LinePointData>[]>(() => {
    const [minValue, maxValue] = yScale.domain();
    return chartLines.map((line) => ({
      data: line.line,
      color: getLineColor(line.variant),
      yAxisId: yAxisId,
      curveType: "linear",
      minValue,
      maxValue,
      plotFromZero: selections.showPlotFromZero,
      connectNulls: false,
    }));
  }, [chartLines, getLineColor, selections.showPlotFromZero, yAxisId, yScale]);

  const xAxis = useMemo<XAxisType[]>(() => {
    const tickFormat = OneRepMaxChartHelpers.getXAxisTickFormat(spanInDays);

    return [
      {
        data: allPoints
          .map(xAccessor)
          .slice()
          .sort((a, b) => a.getTime() - b.getTime()),
        axisId: xAxisId,
        scaleType: "time",
        type: "number",
        orientation: "bottom",
        numTicks: OneRepMaxChartHelpers.getXAxisNumTicks(spanInDays, width),
        tickFormat: (d: Date) => datetime(d).format(tickFormat),
        disableLine: false,
        disableTicks: true,
        // Grouped points sit at the start of their period, which can fall before the selected start date.
        start: datetime(selections.startDate.toDate()).startOf(aggregationUnit).toDate(),
        end: selections.endDate.toDate(),
      },
    ];
  }, [aggregationUnit, allPoints, selections.endDate, selections.startDate, spanInDays, width, xAxisId]);

  const yAxis = useMemo<YAxisType[]>(
    () => [
      {
        axisId: yAxisId,
        scaleType: "linear",
        type: "number",
        orientation: "left",
        tickValues: yScale.ticks(5),
        tickFormat: (d: number) => formatUnit(d, weightMetric),
        disableLine: false,
        disableTicks: true,
        hideZero: !selections.showPlotFromZero,
      },
    ],
    [formatUnit, selections.showPlotFromZero, weightMetric, yAxisId, yScale]
  );

  const renderTooltip = useCallback(
    (data: TooltipData<LinePointData>) => {
      if (!data.tooltipOpen || !data.payload?.length) return null;
      return <OneRepMaxTooltipCard points={data.payload.map((item) => item.datum)} aggregationUnit={aggregationUnit} />;
    },
    [aggregationUnit]
  );

  const getPointLabel = useCallback(
    (date: Date) => datetime(date).format(OneRepMaxChartHelpers.getPeriodDateFormat(aggregationUnit)),
    [aggregationUnit]
  );

  if (!series.length) {
    return <OneRepMaxChartNoData />;
  }

  return (
    <AreaChartProvider
      width={width}
      height={height}
      margin={ONE_REP_MAX_CHART_MARGIN}
      series={series}
      xAxis={xAxis}
      yAxis={yAxis}
      xAccessor={xAccessor}
      yAccessor={yAccessor}
      renderTooltip={renderTooltip}
      getPointLabel={getPointLabel}
    >
      <OneRepMaxChartVariantLabels lines={chartLines} yAxisId={yAxisId} />
    </AreaChartProvider>
  );
}

export default OneRepMaxChartV2;
